// Per-surface switches for the service-quote surfaces. Since the 2026-07-18
// Angi pivot these gate DIRECT AFFILIATE REFERRAL CTAs (outbound CJ-tracked
// links to Angi's request funnel, see data/angi-affiliate.ts), not the paused
// first-party lead form. Outbound links carry no fulfillment burden, so every
// surface is on. The web lead form is dark; bring it back only with a
// fulfillment plan. (Mobile app quote cards are gated separately and still
// post first-party leads to /api/service-leads/submit.)
export const SERVICE_QUOTE_SURFACES = {
  /** The 11 /…-quotes referral landing pages AND their sitemap entries —
   *  coupled on one switch so we never list URLs that 404. */
  landingPages: true,
  /** OFF. Blog in-content card on regional posts (isRegionalServiceSlug).
   *  Retired 2026-08-07 on 1,751 impressions / 1 click (0.06%) over
   *  2026-07-10→08-06, and that one click came from a city-services post, not
   *  from the "best-fertilizer-for-X-in-STATE" bulk that produced ~99% of the
   *  impressions. Those readers are choosing a product; the content slot is
   *  worth more to the Amazon and tool CTAs. sid: blog_inline_service_quote. */
  blogInline: false,
  /** OFF. Blog in-content card on problem-intent posts (weeds/pests/disease —
   *  see getProblemIntentFunnel). Launched 2026-08-01 to test whether
   *  problem-aware blog readers convert like the weed-page and diagnose-result
   *  cards. They do not: 432 impressions / 1 click (0.23%) in its first week
   *  against 3.75% on weed pages and 4.17% on diagnose results over the same
   *  window. A true 3.75% rate would have produced ~16 clicks at n=432, so this
   *  is a rejected hypothesis rather than a thin sample. Retired 2026-08-07,
   *  freeing the slot on ~39 posts. sid: blog_problem_inline. */
  blogProblemInline: false,
  /** OFF pending evidence. Blog in-content card on hire-intent posts
   *  (isHireIntentServiceSlug). Kept wired so a flag flip enables it, but the
   *  case for it does not survive scrutiny: its "14 impressions / 1 click" is a
   *  subset of blogInline's 1,751 impressions, and that click is the SAME
   *  single click that made up blogInline's total. Carving out the bucket that
   *  contains the only conversion guarantees a flattering rate, so this is
   *  selection on the outcome, not a measured 7% surface. The hire-intent
   *  hypothesis is plausible and worth testing, but it needs a denominator
   *  gathered before the fact. Renders from markers hardcoded in the Directus
   *  bodies (the server injector gates on blogInline/blogProblemInline only).
   *  sid: blog_hire_intent. */
  blogHireIntent: false,
  /** Tool result footer CTA (tools in SERVICE_QUOTE_TOOL_MAP). v1: 5.4% CTR,
   *  best on the site. */
  toolFooters: true,
  /** City page lawn-care CTA section. */
  cityPages: true,
  /** Sod calculator result CTA href + mobile sticky dock. */
  sodCalculator: true,
} as const

/** Regional/service-adjacent blog posts ("best-fertilizer-for-x-in-texas") —
 *  the only blog subset where the quote card earned its content slot in v1.
 *  The negative lookahead keeps "-in-lawn"/"-in-your-lawn" problem posts
 *  (moss, wild violet, grubs) out of the regional bucket; those route through
 *  getProblemIntentFunnel instead so their placement analytics stay honest. */
export function isRegionalServiceSlug(slug?: string): boolean {
  return !!slug && /-in-(?!(your-)?(lawn|grass|yard)s?$)[a-z]+(-[a-z]+)*$/.test(slug)
}

/** Hire-intent blog posts: the reader is shopping for a contractor, not for a
 *  product or a how-to. Two shapes exist today — the
 *  "best-lawn-care-services-in-{city}" set and the hire-vs-DIY explainer.
 *  Deliberately an explicit short pattern list rather than a broad "services"
 *  match: the regional fertilizer posts carry service-ish tags too and they
 *  convert at zero, which is what retired blogInline. The blog rail routes its
 *  own sidebar Angi card on the same idea (components/blog/blog-rail-v2.tsx). */
const HIRE_INTENT_SLUG_PATTERNS = [/-services-in-[a-z-]+$/, /hire-a-lawn-care/] as const

export function isHireIntentServiceSlug(slug?: string): boolean {
  return !!slug && HIRE_INTENT_SLUG_PATTERNS.some((pattern) => pattern.test(slug))
}

export const SERVICE_LEAD_SERVICE_TYPES = [
  'lawn_care',
  'weed_control',
  'sprinkler_repair',
  'landscaping',
  'pest_control',
  'tree_removal',
  'sod_installation',
  'aeration_overseeding',
  'mosquito_control',
  'mulch_installation',
  'outdoor_lighting',
] as const

export const SERVICE_LEAD_PROPERTY_TYPES = ['residential', 'commercial'] as const
export const SERVICE_LEAD_TIMELINES = ['asap', 'this_week', 'this_month', 'researching'] as const
export const SERVICE_LEAD_YARD_SIZE_RANGES = [
  'under_2500',
  '2500_5000',
  '5000_10000',
  '10000_20000',
  '20000_plus',
  'not_sure',
] as const
export const SERVICE_LEAD_STATUSES = [
  'new',
  'reviewing',
  'qualified',
  'forwarded',
  'unqualified',
  'spam',
  'credited',
  'closed',
] as const

export type ServiceLeadServiceType = (typeof SERVICE_LEAD_SERVICE_TYPES)[number]
export type ServiceLeadPropertyType = (typeof SERVICE_LEAD_PROPERTY_TYPES)[number]
export type ServiceLeadTimeline = (typeof SERVICE_LEAD_TIMELINES)[number]
export type ServiceLeadYardSizeRange = (typeof SERVICE_LEAD_YARD_SIZE_RANGES)[number]
export type ServiceLeadStatus = (typeof SERVICE_LEAD_STATUSES)[number]

export const SERVICE_LEAD_CONSENT_VERSION = '2026-06-29-lead-provider-v2'
export const SERVICE_LEAD_CONSENT_TEXT =
  'By submitting this form, I agree that What Grass Is This? may contact me about my quote request and may share my request details, including my contact information and any optional photo, with one or more relevant lawn or home service providers after manual review. I agree that What Grass Is This? and those selected providers may contact me about this request by phone, text, or email. Consent is not a condition of purchase. Message and data rates may apply. I agree to the Privacy Policy and Terms.'

export const SERVICE_LEAD_PROPERTY_LABELS: Record<ServiceLeadPropertyType, string> = {
  residential: 'Residential',
  commercial: 'Commercial',
}

export const SERVICE_LEAD_TIMELINE_LABELS: Record<ServiceLeadTimeline, string> = {
  asap: 'As soon as possible',
  this_week: 'This week',
  this_month: 'This month',
  researching: 'Researching options',
}

export const SERVICE_LEAD_YARD_SIZE_LABELS: Record<ServiceLeadYardSizeRange, string> = {
  under_2500: 'Under 2,500 sq ft',
  '2500_5000': '2,500 to 5,000 sq ft',
  '5000_10000': '5,000 to 10,000 sq ft',
  '10000_20000': '10,000 to 20,000 sq ft',
  '20000_plus': '20,000+ sq ft',
  not_sure: 'Not sure',
}

export const SERVICE_LEAD_STATUS_LABELS: Record<ServiceLeadStatus, string> = {
  new: 'New',
  reviewing: 'Reviewing',
  qualified: 'Qualified',
  forwarded: 'Forwarded',
  unqualified: 'Unqualified',
  spam: 'Spam',
  credited: 'Credited',
  closed: 'Closed',
}

export interface ServiceQuoteFunnel {
  serviceType: ServiceLeadServiceType
  slug: string
  path: string
  label: string
  shortLabel: string
  pageTitle: string
  metaTitle: string
  metaDescription: string
  headline: string
  eyebrow: string
  intro: string
  formHeading: string
  formSubcopy: string
  photoPrompt: string
  ctaLabel: string
  goodFit: string[]
  matchingTerms: string[]
}

export const SERVICE_QUOTE_FUNNELS: readonly ServiceQuoteFunnel[] = [
  {
    serviceType: 'lawn_care',
    slug: 'lawn-care',
    path: '/lawn-care-quotes',
    label: 'Lawn care',
    shortLabel: 'Lawn care',
    pageTitle: 'Lawn Care Quotes',
    metaTitle: 'Lawn Care Quotes | What Grass Is This?',
    metaDescription: 'Get free lawn care quotes for mowing, fertilization, seasonal cleanup, and ongoing yard maintenance. We partner with Angi to match you with local pros.',
    headline: 'Get lawn care quotes from local pros',
    eyebrow: 'Free local quotes',
    intro: 'Get matched with local lawn care pros for mowing, cleanup, and repair. Compare free quotes through our partner Angi, no obligation.',
    formHeading: 'Start your lawn care request',
    formSubcopy: 'Share the basics so we can understand the job and keep the handoff useful.',
    photoPrompt: 'Optional: add a photo of the lawn or problem area.',
    ctaLabel: 'Get matched with lawn care pros',
    goodFit: ['Recurring mowing or maintenance', 'Seasonal cleanup', 'General lawn repair or care plans'],
    // 'lawn-care-service' (singular substring) also covers the "best-lawn-care-services-in-{city}"
    // regional posts: the blogInline injection resolves its funnel from the SLUG ONLY, and
    // without this term those hire-intent pages silently got no quote card (2026-08 pilot).
    matchingTerms: ['lawn-care-cost', 'lawn cost', 'cost', 'fertilizer', 'brown-patch', 'lawn disease', 'mowing', 'lawn maintenance', 'seasonal cleanup', 'yard care', 'lawn-care-service'],
  },
  {
    serviceType: 'weed_control',
    slug: 'weed-control',
    path: '/weed-control-quotes',
    label: 'Weed control',
    shortLabel: 'Weed control',
    pageTitle: 'Weed Control Quotes',
    metaTitle: 'Weed Control Quotes | What Grass Is This?',
    metaDescription: 'Get free weed control quotes for broadleaf weeds, crabgrass, nutsedge, and other lawn weed problems. We partner with Angi to match you with local pros.',
    headline: 'Get help with lawn weeds',
    eyebrow: 'Free local quotes',
    intro: 'Describe the weeds you are seeing and compare free quotes from local weed control pros through our partner Angi, no obligation.',
    formHeading: 'Start your weed control request',
    formSubcopy: 'A few details about timing, property type, and weed pressure help us qualify the lead cleanly.',
    photoPrompt: 'Optional: upload a close photo of the weeds or affected area.',
    ctaLabel: 'Get matched with weed control pros',
    goodFit: ['Crabgrass or broadleaf weed pressure', 'Pre-emergent or post-emergent service', 'Weeds returning after DIY treatment'],
    matchingTerms: ['weed', 'weeds', 'crabgrass', 'nutsedge', 'clover', 'dandelion', 'pre-emergent', 'post-emergent', 'herbicide'],
  },
  {
    serviceType: 'sprinkler_repair',
    slug: 'sprinkler-repair',
    path: '/sprinkler-repair-quotes',
    label: 'Sprinkler repair',
    shortLabel: 'Sprinklers',
    pageTitle: 'Sprinkler Repair Quotes',
    metaTitle: 'Sprinkler Repair Quotes | What Grass Is This?',
    metaDescription: 'Get free sprinkler repair quotes for broken heads, leaks, controller problems, and poor coverage. We partner with Angi to match you with local pros.',
    headline: 'Find sprinkler repair help',
    eyebrow: 'Free local quotes',
    intro: 'Broken heads, leaks, or patchy coverage? Get matched with local irrigation pros and compare free quotes through our partner Angi.',
    formHeading: 'Start your sprinkler repair request',
    formSubcopy: 'Include the system issue, timeline, and a photo if it helps explain the problem.',
    photoPrompt: 'Optional: add a photo of a broken head, valve box, dry spot, or controller.',
    ctaLabel: 'Get matched with sprinkler pros',
    goodFit: ['Broken sprinkler heads or valves', 'Leaks or low pressure', 'Controller, zone, or coverage issues'],
    matchingTerms: ['sprinkler', 'sprinklers', 'irrigation', 'watering', 'dry spots', 'controller', 'valve', 'zone'],
  },
  {
    serviceType: 'landscaping',
    slug: 'landscaping',
    path: '/landscaping-quotes',
    label: 'Landscaping',
    shortLabel: 'Landscaping',
    pageTitle: 'Landscaping Quotes',
    metaTitle: 'Landscaping Quotes | What Grass Is This?',
    metaDescription: 'Get free landscaping quotes for planting beds, yard refreshes, cleanup, grading, and outdoor improvements. We partner with Angi to match you with local pros.',
    headline: 'Get landscaping quotes from local pros',
    eyebrow: 'Free local quotes',
    intro: 'Share your outdoor project and compare free quotes from local landscaping pros through our partner Angi, no obligation.',
    formHeading: 'Start your landscaping request',
    formSubcopy: 'A short description of the project scope keeps the request practical for manual review.',
    photoPrompt: 'Optional: upload a photo of the project area.',
    ctaLabel: 'Get matched with landscaping pros',
    goodFit: ['Bed refreshes and planting', 'Yard cleanup or redesign', 'Small grading or outdoor improvement projects'],
    matchingTerms: ['landscaping', 'landscape', 'planting', 'yard design', 'beds', 'cleanup', 'hardscape'],
  },
  {
    serviceType: 'pest_control',
    slug: 'pest-control',
    path: '/pest-control-quotes',
    label: 'Pest control',
    shortLabel: 'Pest control',
    pageTitle: 'Pest Control Quotes',
    metaTitle: 'Pest Control Quotes | What Grass Is This?',
    metaDescription: 'Get free pest control quotes for lawn insects, grubs, ants, and other outdoor pest problems. We partner with Angi to match you with local pros.',
    headline: 'Get outdoor pest control help',
    eyebrow: 'Free local quotes',
    intro: 'Tell local pest control pros what you are seeing and compare free quotes through our partner Angi, no obligation.',
    formHeading: 'Start your pest control request',
    formSubcopy: 'A quick description helps separate lawn pests from broader home pest issues.',
    photoPrompt: 'Optional: add a photo of the pest, damage, or affected lawn area.',
    ctaLabel: 'Get matched with pest control pros',
    goodFit: ['Grubs or lawn insects', 'Ants or outdoor pest pressure', 'Recurring yard pest issues'],
    matchingTerms: ['pest', 'pests', 'grub', 'grubs', 'ants', 'armyworms', 'chinch bugs', 'lawn insects'],
  },
  {
    serviceType: 'tree_removal',
    slug: 'tree-removal',
    path: '/tree-removal-quotes',
    label: 'Tree removal',
    shortLabel: 'Tree removal',
    pageTitle: 'Tree Removal Quotes',
    metaTitle: 'Tree Removal Quotes | What Grass Is This?',
    metaDescription: 'Get free tree removal quotes for dead, leaning, storm-damaged, or unwanted trees. We partner with Angi to match you with local pros.',
    headline: 'Get tree removal quotes from local pros',
    eyebrow: 'Free local quotes',
    intro: 'Describe the tree, access, and urgency and compare free quotes from local tree pros through our partner Angi.',
    formHeading: 'Start your tree removal request',
    formSubcopy: 'Tree work depends heavily on access, height, and risk. Details and photos help.',
    photoPrompt: 'Optional: add a wide photo showing the tree and nearby structures.',
    ctaLabel: 'Get matched with tree removal pros',
    goodFit: ['Dead or storm-damaged trees', 'Leaning or unsafe trees', 'Stump or cleanup questions'],
    matchingTerms: ['tree removal', 'tree service', 'stump', 'storm damage', 'dead tree', 'leaning tree'],
  },
  {
    serviceType: 'sod_installation',
    slug: 'sod-installation',
    path: '/sod-installation-quotes',
    label: 'Sod installation',
    shortLabel: 'Sod',
    pageTitle: 'Sod Installation Quotes',
    metaTitle: 'Sod Installation Quotes | What Grass Is This?',
    metaDescription: 'Get free sod installation quotes for new lawns, patch repairs, and full yard replacements. We partner with Angi to match you with local pros.',
    headline: 'Get sod installation quotes from local pros',
    eyebrow: 'Free local quotes',
    intro: 'Replacing a full lawn or repairing a section? Get matched with local sod installers and compare free quotes through our partner Angi.',
    formHeading: 'Start your sod request',
    formSubcopy: 'Include approximate area, timeline, and any existing lawn condition details.',
    photoPrompt: 'Optional: upload a photo of the area that needs sod.',
    ctaLabel: 'Get matched with sod installers',
    goodFit: ['New lawn installation', 'Large bare patches', 'Full lawn replacement after damage or grading'],
    matchingTerms: ['sod', 'sod installation', 'new lawn', 'bare patches', 'lawn replacement', 'grass installation'],
  },
  {
    serviceType: 'aeration_overseeding',
    slug: 'aeration-overseeding',
    path: '/aeration-overseeding-quotes',
    label: 'Aeration and overseeding',
    shortLabel: 'Aeration',
    pageTitle: 'Aeration and Overseeding Quotes',
    metaTitle: 'Aeration and Overseeding Quotes | What Grass Is This?',
    metaDescription: 'Get free aeration and overseeding quotes for compacted soil, thinning lawns, and seasonal renovation. We partner with Angi to match you with local pros.',
    headline: 'Get aeration and overseeding quotes from local pros',
    eyebrow: 'Free local quotes',
    intro: 'Compacted soil or a thinning lawn? Get matched with local pros for aeration, overseeding, or both through our partner Angi.',
    formHeading: 'Start your aeration request',
    formSubcopy: 'Season and grass type matter, so tell us what you know and we will keep the request honest.',
    photoPrompt: 'Optional: add a photo of thinning or compacted areas.',
    ctaLabel: 'Get matched with aeration pros',
    goodFit: ['Compacted soil', 'Thin cool-season lawns', 'Fall renovation planning'],
    matchingTerms: ['aeration', 'aerate', 'overseeding', 'overseed', 'compaction', 'thin lawn', 'lawn renovation'],
  },
  {
    serviceType: 'mosquito_control',
    slug: 'mosquito-control',
    path: '/mosquito-control-quotes',
    label: 'Mosquito control',
    shortLabel: 'Mosquitoes',
    pageTitle: 'Mosquito Control Quotes',
    metaTitle: 'Mosquito Control Quotes | What Grass Is This?',
    metaDescription: 'Get free mosquito control quotes for yards, patios, and outdoor living areas. We partner with Angi to match you with local pros.',
    headline: 'Get mosquito control quotes from local pros',
    eyebrow: 'Free local quotes',
    intro: 'Take back your yard. Get matched with local mosquito control pros and compare free quotes through our partner Angi.',
    formHeading: 'Start your mosquito control request',
    formSubcopy: 'A few details about the yard and timeline help us qualify the request.',
    photoPrompt: 'Optional: add a photo of standing water or the outdoor area.',
    ctaLabel: 'Get matched with mosquito pros',
    goodFit: ['Backyard mosquito pressure', 'Patio or outdoor event concerns', 'Seasonal mosquito programs'],
    matchingTerms: ['mosquito', 'mosquitoes', 'standing water', 'outdoor pests', 'backyard pests'],
  },
  {
    serviceType: 'mulch_installation',
    slug: 'mulch-installation',
    path: '/mulch-installation-quotes',
    label: 'Mulch installation',
    shortLabel: 'Mulch',
    pageTitle: 'Mulch Installation Quotes',
    metaTitle: 'Mulch Installation Quotes | What Grass Is This?',
    metaDescription: 'Get free mulch installation quotes for garden beds, tree rings, and curb appeal refreshes. We partner with Angi to match you with local pros.',
    headline: 'Get mulch installation quotes from local pros',
    eyebrow: 'Free local quotes',
    intro: 'Tell local pros how many beds need fresh mulch and compare free quotes through our partner Angi, no obligation.',
    formHeading: 'Start your mulch request',
    formSubcopy: 'A project photo or quick bed count makes this much easier to qualify.',
    photoPrompt: 'Optional: add a photo of the beds or tree rings.',
    ctaLabel: 'Get matched with mulch pros',
    goodFit: ['Seasonal bed refreshes', 'Mulch delivery and spreading', 'Bed cleanup or edging before mulch'],
    matchingTerms: ['mulch', 'mulching', 'bed refresh', 'garden beds', 'tree rings', 'edging'],
  },
  {
    serviceType: 'outdoor_lighting',
    slug: 'outdoor-lighting',
    path: '/outdoor-lighting-quotes',
    label: 'Outdoor lighting',
    shortLabel: 'Lighting',
    pageTitle: 'Outdoor Lighting Quotes',
    metaTitle: 'Outdoor Lighting Quotes | What Grass Is This?',
    metaDescription: 'Get free outdoor lighting quotes for paths, patios, landscape beds, and curb appeal projects. We partner with Angi to match you with local pros.',
    headline: 'Get outdoor lighting quotes from local pros',
    eyebrow: 'Free local quotes',
    intro: 'Describe the areas you want lit and compare free quotes from local lighting pros through our partner Angi, no obligation.',
    formHeading: 'Start your lighting request',
    formSubcopy: 'A quick project description helps separate simple fixtures from full landscape lighting design.',
    photoPrompt: 'Optional: upload a photo of the front yard, path, patio, or beds.',
    ctaLabel: 'Get matched with lighting pros',
    goodFit: ['Path and walkway lighting', 'Landscape accent lighting', 'Patio or curb appeal projects'],
    matchingTerms: ['outdoor lighting', 'landscape lighting', 'path lights', 'walkway lights', 'accent lighting', 'patio lighting'],
  },
] as const

export const SERVICE_QUOTE_FUNNEL_BY_TYPE = SERVICE_QUOTE_FUNNELS.reduce(
  (acc, funnel) => {
    acc[funnel.serviceType] = funnel
    return acc
  },
  {} as Record<ServiceLeadServiceType, ServiceQuoteFunnel>
)

export const SERVICE_QUOTE_FUNNEL_BY_SLUG = SERVICE_QUOTE_FUNNELS.reduce(
  (acc, funnel) => {
    acc[funnel.slug] = funnel
    return acc
  },
  {} as Record<string, ServiceQuoteFunnel>
)

export const SERVICE_QUOTE_FUNNEL_BY_PATH = SERVICE_QUOTE_FUNNELS.reduce(
  (acc, funnel) => {
    acc[funnel.path] = funnel
    return acc
  },
  {} as Record<string, ServiceQuoteFunnel>
)

export function getServiceQuoteFunnel(serviceType: ServiceLeadServiceType): ServiceQuoteFunnel {
  return SERVICE_QUOTE_FUNNEL_BY_TYPE[serviceType]
}

export function getServiceQuoteFunnelBySlug(slug: string): ServiceQuoteFunnel | undefined {
  return SERVICE_QUOTE_FUNNEL_BY_SLUG[slug]
}

export function getServiceQuoteFunnelByPath(path: string): ServiceQuoteFunnel | undefined {
  return SERVICE_QUOTE_FUNNEL_BY_PATH[path]
}

export function getServiceQuoteFunnelForContent(
  slug?: string,
  category?: string,
  tags: string[] = []
): ServiceQuoteFunnel | null {
  const haystack = [slug, category, ...tags].filter(Boolean).join(' ').toLowerCase()
  if (!haystack) return null

  const ordered = [
    'sprinkler_repair',
    'weed_control',
    'aeration_overseeding',
    'sod_installation',
    'pest_control',
    'mosquito_control',
    'tree_removal',
    'mulch_installation',
    'outdoor_lighting',
    'landscaping',
    'lawn_care',
  ] as const

  for (const serviceType of ordered) {
    const funnel = SERVICE_QUOTE_FUNNEL_BY_TYPE[serviceType]
    if (funnel.matchingTerms.some((term) => haystack.includes(term))) {
      return funnel
    }
  }

  return null
}

export function getServiceQuotePathForIntent(slug?: string, category?: string, tags: string[] = []): string | null {
  return getServiceQuoteFunnelForContent(slug, category, tags)?.path ?? null
}

/**
 * Problem-intent matching for the blog inline card. Deliberately NARROWER than
 * getServiceQuoteFunnelForContent: that matcher exists to route an already-shown
 * CTA to the best service and includes broad research terms ('cost',
 * 'fertilizer', 'mowing', 'watering') that would put a quote card on how-to
 * content where it demonstrably dies (0/373 on the v2 regional placement).
 * This list only matches content about an active lawn PROBLEM — the reader
 * already has weeds/grubs/fungus and wants them gone, which is the same intent
 * moment the weed-page and diagnose-result cards convert on.
 *
 * Order matters: first match wins, most specific service first.
 */
const PROBLEM_INTENT_MATCHERS: readonly {
  serviceType: ServiceLeadServiceType
  terms: readonly string[]
}[] = [
  // Pests before weeds so "get-rid-of-chinch-bugs" routes to the pest funnel
  // and the generic 'get-rid-of' weed catch-all doesn't swallow it.
  {
    serviceType: 'pest_control',
    terms: [
      'grub',
      'armyworm',
      'chinch',
      'sod webworm',
      'sod-webworm',
      'billbug',
      'mole cricket',
      'mole-cricket',
      'lawn pest',
      'lawn-pest',
    ],
  },
  {
    serviceType: 'weed_control',
    terms: [
      'get-rid-of',
      'crabgrass',
      'nutsedge',
      'dandelion',
      'creeping-charlie',
      'quack-grass',
      'poa-annua',
      'doveweed',
      'dollarweed',
      'spotted-spurge',
      'wild violet',
      'wild-violet',
      'dallisgrass',
      'identification-removal',
      'weed-management',
      'weeds-and-how-to-remove',
      'weed removal',
    ],
  },
  {
    serviceType: 'mosquito_control',
    terms: ['mosquito'],
  },
  {
    // No disease-specific Angi category; fungus/disease posts route to the
    // general Lawn & Garden Care funnel like diagnose-result disease hits do.
    serviceType: 'lawn_care',
    terms: [
      'disease',
      'brown patch',
      'brown-patch',
      'dollar spot',
      'dollar-spot',
      'fairy ring',
      'fairy-ring',
      'pythium',
      'red thread',
      'red-thread',
      'snow mold',
      'lawn-mold',
      'fungus',
      'dead-spot',
      'lawn-turning-yellow',
      'brown-spots',
      'dog-urine',
    ],
  },
] as const

// A problem term alone isn't enough: many product posts NAME the problem
// ("best crabgrass killer", "bioadvanced grub killer instructions"). Those
// readers are DIY-committed and the pages are the site's core Amazon affiliate
// surfaces — a hire-a-pro card there fights the product picks and misreads the
// intent. Slug-only guard (tags on product posts also carry problem terms).
const DIY_PRODUCT_SLUG_RE =
  /(^|-)(best|top|reviews?|buyers?|vs|instructions?|application|apply|how-to-use|spreader|printable)(-|$)|scotts|ortho|spectracide|roundup|tenacity|bioadvanced|milorganite|ironite|pennington|yard-mastery/

export function getProblemIntentFunnel(
  slug?: string,
  category?: string,
  tags: string[] = []
): ServiceQuoteFunnel | null {
  if (slug && DIY_PRODUCT_SLUG_RE.test(slug.toLowerCase())) return null

  const haystack = [slug, category, ...tags].filter(Boolean).join(' ').toLowerCase()
  if (!haystack) return null

  for (const { serviceType, terms } of PROBLEM_INTENT_MATCHERS) {
    if (terms.some((term) => haystack.includes(term))) {
      return SERVICE_QUOTE_FUNNEL_BY_TYPE[serviceType]
    }
  }
  return null
}
